import * as React from 'react'

import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import Workflow from '~/client/src/shared/models/Workflow'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import PermitWorkflowFieldsStore from '~/client/src/shared/stores/ui/PermitFieldsStores/PermitWorkflowFields.store'

import Localization from '../../../localization/LocalizationManager'
import WorkflowSummaryService, {
  IWorkflowSummaryUsage,
  WorkflowSummaryModelId,
  WORKFLOW_SUMMARY_MODELS,
} from '../../../services/WorkflowSummary.service'
import { mapWorkflowSummaryRequest } from '../../../utils/workflowSummaryMapper'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../shadcn-ui/dropdown-menu'

interface IProps {
  permit: Workflow
  workflowFieldsStore: PermitWorkflowFieldsStore
  companiesStore?: CompaniesStore
  projectMembersStore?: ProjectMembersStore
  locationAttributesStore?: LocationAttributesStore
}

export default function WorkflowSummary({
  permit,
  workflowFieldsStore,
  companiesStore,
  projectMembersStore,
  locationAttributesStore,
}: IProps): JSX.Element {
  const [aiHeadline, setAiHeadline] = React.useState<string>(null)
  const [aiStatus, setAiStatus] = React.useState<string>(null)
  const [aiSummary, setAiSummary] = React.useState<string>(null)
  const [aiFlags, setAiFlags] = React.useState<string[]>([])
  const [aiFacts, setAiFacts] = React.useState<string[]>([])
  const [aiWarningCount, setAiWarningCount] = React.useState<number>(0)
  const [aiSummaryError, setAiSummaryError] = React.useState<string>(null)
  const [isAiSummaryLoading, setIsAiSummaryLoading] = React.useState(true)
  const [aiSummaryUsage, setAiSummaryUsage] =
    React.useState<IWorkflowSummaryUsage>(null)
  const [aiSelectedModel, setAiSelectedModel] =
    React.useState<WorkflowSummaryModelId>(WORKFLOW_SUMMARY_MODELS[0].id)

  const requestIdRef = React.useRef(0)
  const hasInitializedRef = React.useRef(false)

  const generateAiSummary = React.useCallback(
    async (model: WorkflowSummaryModelId) => {
      const requestId = ++requestIdRef.current

      setIsAiSummaryLoading(true)
      setAiSummaryError(null)

      try {
        const result = await WorkflowSummaryService.summarize(
          mapWorkflowSummaryRequest(permit, workflowFieldsStore, {
            companiesStore,
            projectMembersStore,
            locationAttributesStore,
          }),
          model,
        )

        if (requestId !== requestIdRef.current) {
          return
        }

        setAiHeadline(result.headline)
        setAiStatus(result.status)
        setAiSummary(result.compact?.prose || null)
        setAiFlags(result.flags || [])
        setAiFacts(result.facts || [])
        setAiWarningCount(result.compact?.warning_count || 0)
        setAiSummaryUsage(result.usage || null)
      } catch (error) {
        if (requestId !== requestIdRef.current) {
          return
        }

        setAiSummaryError(
          error instanceof Error
            ? error.message
            : Localization.translator.failed,
        )
      } finally {
        if (requestId === requestIdRef.current) {
          setIsAiSummaryLoading(false)
        }
      }
    },
    [
      companiesStore,
      locationAttributesStore,
      permit,
      projectMembersStore,
      workflowFieldsStore,
    ],
  )

  React.useEffect(() => {
    if (hasInitializedRef.current) {
      return
    }

    hasInitializedRef.current = true
    void generateAiSummary(WORKFLOW_SUMMARY_MODELS[0].id)

    return () => {
      requestIdRef.current += 1
    }
  }, [generateAiSummary])

  const onRegenerateWithModel = React.useCallback(
    (model: WorkflowSummaryModelId) => {
      setAiSelectedModel(model)
      void generateAiSummary(model)
    },
    [generateAiSummary],
  )

  const hasResult = !!aiHeadline

  const renderRegenerateMenu = () => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={isAiSummaryLoading}
          className="tw:inline-flex tw:h-auto tw:items-center tw:rounded-none tw:border-0 tw:bg-transparent tw:px-0 tw:text-primary tw:outline-none tw:transition-colors tw:hover:text-primary/90 tw:disabled:cursor-not-allowed tw:disabled:opacity-50"
        >
          Regenerate
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {WORKFLOW_SUMMARY_MODELS.map(m => (
            <DropdownMenuItem
              key={m.id}
              onClick={() => onRegenerateWithModel(m.id)}
            >
              <span className="tw:flex tw:min-w-0 tw:flex-col">
                <span>{m.label}</span>
                {aiSelectedModel === m.id ? (
                  <span className="tw:text-xs tw:text-muted-foreground">
                    Current
                  </span>
                ) : null}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  if (isAiSummaryLoading && !hasResult) {
    return (
      <div className="tw:mt-4 tw:flex tw:items-center tw:justify-center tw:py-8">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="tw:mt-4">
      {!!aiSummaryError && (
        <div className="tw:flex tw:items-center tw:gap-2">
          <span className="tw:text-body-md tw:text-destructive">
            {aiSummaryError}
          </span>
          {renderRegenerateMenu()}
        </div>
      )}

      {hasResult && (
        <div className="tw:rounded-2xl tw:bg-muted tw:p-4">
          <div className="tw:flex tw:items-center tw:justify-between tw:mb-3">
            <span className="tw:text-label-md tw:text-muted-foreground">
              AI {Localization.translator.summary}
            </span>
            {isAiSummaryLoading ? (
              <Spinner size="sm" />
            ) : (
              renderRegenerateMenu()
            )}
          </div>

          <span className="tw:text-title-md tw:text-foreground">
            {aiHeadline}
          </span>
          <div className="tw:mt-1">
            <span className="tw:inline-flex tw:items-center tw:rounded-full tw:bg-secondary tw:px-2.5 tw:py-0.5 tw:text-label-sm tw:text-secondary-foreground">
              {aiStatus}
            </span>
          </div>

          <div className="tw:mt-3 tw:text-body-md tw:text-foreground tw:leading-relaxed">
            {aiSummary}
          </div>

          {!!(aiFlags.length || aiWarningCount) && (
            <div className="tw:mt-3 tw:rounded-xl tw:bg-destructive/10 tw:px-3 tw:py-2.5">
              <div className="tw:flex tw:items-center tw:gap-2">
                <span className="tw:text-label-sm tw:font-medium tw:text-destructive">
                  Needs attention
                </span>
                {!!aiWarningCount && (
                  <span className="tw:inline-flex tw:items-center tw:rounded-full tw:bg-destructive tw:px-2 tw:py-0.5 tw:text-label-sm tw:text-destructive-foreground">
                    {aiWarningCount}
                  </span>
                )}
              </div>
              <div className="tw:mt-1.5 tw:flex tw:flex-col tw:gap-1.5">
                {aiFlags.map((item, idx) => (
                  <div
                    key={idx}
                    className="tw:flex tw:items-start tw:gap-2 tw:text-body-sm tw:text-destructive"
                  >
                    <span className="tw:shrink-0">⚠️</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!!aiFacts.length && (
            <div className="tw:mt-3 tw:flex tw:flex-col tw:gap-1.5">
              {aiFacts.map((fact, idx) => (
                <div
                  key={idx}
                  className="tw:flex tw:items-start tw:gap-2.5 tw:text-body-sm tw:text-muted-foreground"
                >
                  <span className="tw:mt-[7px] tw:h-1.5 tw:w-1.5 tw:shrink-0 tw:rounded-full tw:bg-border" />
                  <span>{fact}</span>
                </div>
              ))}
            </div>
          )}

          {!!aiSummaryUsage && (
            <div className="tw:mt-4 tw:flex tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-1 tw:border-t tw:border-border/30 tw:pt-3 tw:text-label-sm tw:text-muted-foreground/60">
              <span>{aiSummaryUsage.model}</span>
              <span>·</span>
              <span>{aiSummaryUsage.promptTokens} prompt</span>
              <span>·</span>
              <span>{aiSummaryUsage.completionTokens} completion</span>
              <span>·</span>
              <span>{aiSummaryUsage.totalTokens} total tokens</span>
              {aiSummaryUsage.costUsd != null && (
                <>
                  <span>·</span>
                  <span>${aiSummaryUsage.costUsd.toFixed(6)}</span>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
